// File: src/globalComponents/NavigationCache.js

// Profile cache constants
const PROFILE_CACHE_KEY_PREFIX = 'profile_cache_';
const PROFILE_CACHE_EXPIRY = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

// Nav state cache constants
const NAV_STATE_CACHE_KEY_PREFIX = 'nav_state_cache_';
const NAV_STATE_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// Category cache constants (Added this)
const CATEGORY_CACHE_KEY_PREFIX = 'category_cache_';
const CATEGORY_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// ========== PROFILE CACHE ==========
export const ProfileCache = {
  // Save profile data to cache
  set: (consoleType, profileData) => {
    try {
      const cacheKey = `${PROFILE_CACHE_KEY_PREFIX}${consoleType}`;
      const cacheData = {
        data: profileData,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving profile to cache:', error);
    }
  },

  // Get profile data from cache
  get: (consoleType) => {
    try {
      const cacheKey = `${PROFILE_CACHE_KEY_PREFIX}${consoleType}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - cacheData.timestamp > PROFILE_CACHE_EXPIRY) {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return cacheData.data;
    } catch (error) {
      console.error('Error reading profile from cache:', error);
      return null;
    }
  },

  // Get cached data immediately (for instant paint) while always kicking
  // off a fresh backend fetch in the background and updating the cache +
  // caller when it resolves. See fetchProfileDetails in ConsoleNavigation.jsx
  // / AdminConsoleNavigation.jsx for why: `get()` alone skips the network
  // call while the 3h TTL is valid, so a role changed elsewhere isn't
  // picked up until logout clears this cache.
  //
  // fetcher: () => Promise<profileData>
  // onUpdate: (freshData) => void — called once the background fetch resolves
  getWithRevalidate: (consoleType, fetcher, onUpdate) => {
    const cached = ProfileCache.get(consoleType);
    Promise.resolve()
      .then(fetcher)
      .then((freshData) => {
        if (!freshData) return;
        ProfileCache.set(consoleType, freshData);
        if (onUpdate) onUpdate(freshData);
      })
      .catch((error) => {
        console.error('Error revalidating profile cache:', error);
      });
    return cached;
  },

  // Clear profile cache for specific console
  clear: (consoleType) => {
    try {
      const cacheKey = `${PROFILE_CACHE_KEY_PREFIX}${consoleType}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing profile cache:', error);
    }
  },

  // Clear all profile caches
  clearAll: () => {
    try {
      ['admin-console', 'iam-console'].forEach(consoleType => {
        ProfileCache.clear(consoleType);
      });
    } catch (error) {
      console.error('Error clearing all profile caches:', error);
    }
  }
};

// ========== NAV STATE CACHE ==========
export const NavStateCache = {
  // Save nav state to cache
  set: (consoleType, navState) => {
    try {
      const cacheKey = `${NAV_STATE_CACHE_KEY_PREFIX}${consoleType}`;
      const cacheData = {
        data: navState,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving nav state to cache:', error);
    }
  },

  // Get nav state from cache
  get: (consoleType) => {
    try {
      const cacheKey = `${NAV_STATE_CACHE_KEY_PREFIX}${consoleType}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - cacheData.timestamp > NAV_STATE_CACHE_EXPIRY) {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return cacheData.data;
    } catch (error) {
      console.error('Error reading nav state from cache:', error);
      return null;
    }
  },

  // Clear nav state cache for specific console
  clear: (consoleType) => {
    try {
      const cacheKey = `${NAV_STATE_CACHE_KEY_PREFIX}${consoleType}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing nav state cache:', error);
    }
  },

  // Clear all nav state caches
  clearAll: () => {
    try {
      ['admin-console', 'iam-console'].forEach(consoleType => {
        NavStateCache.clear(consoleType);
      });
    } catch (error) {
      console.error('Error clearing all nav state caches:', error);
    }
  }
};

// ========== CATEGORY CACHE (NEW ADDITION) ==========
export const CategoryCache = {
  // Save category data to cache
  set: (key, data) => {
    try {
      const cacheKey = `${CATEGORY_CACHE_KEY_PREFIX}${key}`;
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving category to cache:', error);
    }
  },

  // Get category data from cache
  get: (key) => {
    try {
      const cacheKey = `${CATEGORY_CACHE_KEY_PREFIX}${key}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - cacheData.timestamp > CATEGORY_CACHE_EXPIRY) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return cacheData.data;
    } catch (error) {
      console.error('Error reading category from cache:', error);
      return null;
    }
  },

  // Clear specific category cache
  clear: (key) => {
    try {
      const cacheKey = `${CATEGORY_CACHE_KEY_PREFIX}${key}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing category cache:', error);
    }
  }
};